import React, { useState, useRef } from "react";
import { App } from "antd";
import {
  UploadOutlined, FilePdfOutlined, CompressOutlined,
  DownloadOutlined, DeleteOutlined, CheckCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import { downloadAuthenticatedFile } from "../../utils/downloadFile";
import "../PdfTools.css";
import "./CompressPdf.css";

const COMPRESSION_OPTIONS = [
  { value: 3, label: "Extreme Compression", desc: "Smallest file size, lower quality" },
  { value: 2, label: "Recommended",         desc: "Best balance of size and quality", default: true },
  { value: 1, label: "Low Compression",     desc: "Larger file, higher quality" },
];

const CompressPdf = () => {
  const { message } = App.useApp();
  const [file,         setFile]         = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [downloading,  setDownloading]  = useState(false);
  const [downloadUrl,  setDownloadUrl]  = useState("");
  const [resultStats,  setResultStats]  = useState(null);
  const [dragOver,     setDragOver]     = useState(false);
  const [compression,  setCompression]  = useState(2); // default = recommended
  const fileRef = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  const pickFile = (incoming) => {
    const f = Array.from(incoming).find((f) =>
      f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (!f) { message.error("Please select a PDF file."); return; }
    setFile(f);
    setDownloadUrl("");
    setDownloading(false);
    setResultStats(null);
  };

  const reset = () => {
    setFile(null);
    setDownloadUrl("");
    setDownloading(false);
    setResultStats(null);
  };

  const formatBytes = (bytes = 0) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleCompress = async () => {
    if (!file) { message.warning("Please select a PDF file."); return; }
    setUploading(true);
    setDownloadUrl("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("Source", isModule ? "Private-Web" : "Public-Web");
    formData.append("compressionOption", compression.toString());

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/compress-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (!res.data.error && res.data.data?.downloadUrl) {
        const data = res.data.data;
        setDownloadUrl(data.downloadUrl);
        setResultStats(data);
        message.success(res.data.message || "PDF compressed successfully!");
      } else {
        message.error(res.data.message || "Compression failed.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  const downloadCompressedPdf = async () => {
    if (!downloadUrl || downloading) return;
    setDownloading(true);

    try {
      await downloadAuthenticatedFile(downloadUrl, {
        token: localStorage.getItem("token") ?? "",
        fileName: resultStats?.fileName || "compressed.pdf",
      });
    } catch {
      message.error("Unable to download compressed PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`pt-root ${isModule ? "pt-module" : ""}`}>
        <div className="pt-inner">

          {/* Header */}
          <div className="pt-header">
            <div className="pt-header-icon" style={{ background: "#f0fdf4" }}>
              <CompressOutlined style={{ color: "#1a7a43" }} />
            </div>
            <div>
              <h1 className="pt-title">Compress PDF</h1>
              <p className="pt-subtitle">Reduce the size of your PDF file while keeping it readable.</p>
            </div>
          </div>

          {/* Drop zone */}
          {!downloadUrl && !file && (
            <div
              className={`pt-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
                onChange={(e) => { pickFile(e.target.files); e.target.value = ""; }} />
              <FilePdfOutlined className="pt-dropzone-icon" />
              <div className="pt-dropzone-text">Click to select or drag your PDF here</div>
              <div className="pt-dropzone-hint">Only PDF files supported</div>
              <button className="pt-choose-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                <UploadOutlined /> Choose File
              </button>
            </div>
          )}

          {/* File selected */}
          {file && !downloadUrl && (
            <div className="pt-file-row">
              <FilePdfOutlined className="pt-file-icon" />
              <span className="pt-file-name">{file.name}</span>
              <span className="pt-file-size">{(file.size / 1024).toFixed(0)} KB</span>
              <button className="pt-file-remove" onClick={reset}><DeleteOutlined /></button>
            </div>
          )}

          {/* Compression options */}
          {file && !downloadUrl && (
            <div className="cp-options">
              <div className="cp-options-title">Compression Level</div>
              {COMPRESSION_OPTIONS.map((opt) => (
                <label key={opt.value} className={`cp-option ${compression === opt.value ? "selected" : ""}`}>
                  <input
                    type="radio"
                    name="compression"
                    value={opt.value}
                    checked={compression === opt.value}
                    onChange={() => setCompression(opt.value)}
                  />
                  <div className="cp-option-dot" />
                  <div className="cp-option-text">
                    <span className="cp-option-label">
                      {opt.label}
                      {opt.default && <span className="cp-recommended">Recommended</span>}
                    </span>
                    <span className="cp-option-desc">{opt.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Success */}
          {downloadUrl && (
            <div className="pt-success">
              <CheckCircleFilled className="pt-success-icon" />
              <div className="pt-success-title">Compression Complete!</div>
              <p className="pt-success-sub">Your PDF has been compressed successfully.</p>
              {resultStats && (
                <div className="cp-result-stats">
                  <div>
                    <span>Original Size</span>
                    <strong>{formatBytes(resultStats.originalSizeBytes)}</strong>
                  </div>
                  <div>
                    <span>Compressed Size</span>
                    <strong>{formatBytes(resultStats.compressedSizeBytes)}</strong>
                  </div>
                  <div>
                    <span>Saved</span>
                    <strong>{formatBytes(resultStats.savedSizeBytes)}</strong>
                  </div>
                  <div>
                    <span>Reduction</span>
                    <strong>{Number(resultStats.compressionPercentage || 0).toFixed(1)}%</strong>
                  </div>
                </div>
              )}
              <div className="pt-success-actions">
                <button
                  type="button"
                  className="pt-download-btn"
                  onClick={downloadCompressedPdf}
                  disabled={downloading}
                >
                  {downloading ? <span className="pt-spinner" /> : <DownloadOutlined />}
                  {downloading ? "Downloading..." : "Download Compressed PDF"}
                </button>
                <button className="pt-restart-btn" onClick={reset}>Compress another</button>
              </div>
            </div>
          )}

          {/* Action */}
          {!downloadUrl && (
            <button className="pt-action-btn" onClick={handleCompress} disabled={!file || uploading}>
              {uploading ? <span className="pt-spinner" /> : <CompressOutlined />}
              {uploading ? "Compressing…" : "Compress PDF"}
            </button>
          )}
        </div>
      </div>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default CompressPdf;
